import { computed, toValue } from 'vue'
import { useRoute } from 'nuxt/app'

import { useAppAccess } from '@common/app-access-layer/composables/useAppAccess'
import { useAuth } from '@common/auth-layer/composables/useAuth'
import { useLocation } from '@common/location-layer/composables/useLocation'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

import { firstRouteValue } from '../utils/event-detail.utils'
import { toExhibitorCenterSectionId } from '../utils/exhibitor-center-nav.utils'
import { useEventsQueryStore } from '../stores/events.query-store'
import { toExhibitorMemberships } from '../utils/event-people.utils'
import type { UseExhibitorAdminContext } from '../types/use-exhibitor-admin-context'

/**
 * Which exhibitors the viewer administers for the event in the route, plus the two navigations between
 * the public event page and the exhibitor center. Shared by the portal shell (which decides whether to
 * offer the center at all) and by the center's own controller.
 */
export const useExhibitorAdminContext = (): UseExhibitorAdminContext => {
  const route = useRoute()
  const { CPNavigateTo } = useCPNavigation()
  const { canAccessApp } = useAppAccess()
  const auth = useAuth()
  const { locationId } = useLocation()

  const eventId = computed(() => firstRouteValue(route.params.eventId))
  const canViewEvents = computed(() => canAccessApp('events'))
  const viewerId = computed(() => (typeof auth.store.contactId === 'string' ? auth.store.contactId.trim() : ''))
  const resolvedLocationId = computed(() => String(toValue(locationId) ?? '').trim())
  const hasQueryIdentity = computed(() => Boolean(viewerId.value && resolvedLocationId.value && canViewEvents.value))
  const queryEventId = computed(() => (hasQueryIdentity.value && eventId.value ? eventId.value : undefined))

  const contactId = computed(() => viewerId.value || null)
  const membershipsQuery = useEventsQueryStore(contactId).fetchEventExhibitorMembershipsQuery(queryEventId, hasQueryIdentity)

  const memberships = computed(() => toExhibitorMemberships(membershipsQuery.data.value?.memberships ?? []))
  const adminMemberships = computed(() => memberships.value.filter(membership => membership.role === 'admin'))
  const hasAdminAccess = computed(() => adminMemberships.value.length > 0)
  const isSettled = computed(() => membershipsQuery.status.value === 'success' || membershipsQuery.status.value === 'error')

  const isAdminOf = (exhibitorId: string) => adminMemberships.value.some(membership => membership.exhibitorId === exhibitorId.trim())
  /** Any role on the exhibitor's team, admin or member. */
  const isMemberOf = (exhibitorId: string) => memberships.value.some(membership => membership.exhibitorId === exhibitorId.trim())

  const requestedExhibitorId = computed(() => firstRouteValue(route.query.exhibitor))
  /** The `?exhibitor` the viewer actually administers, else their first company. */
  const activeExhibitorId = computed(() => {
    const requested = requestedExhibitorId.value
    if (requested && isAdminOf(requested)) return requested
    return adminMemberships.value[0]?.exhibitorId ?? ''
  })

  /** Entering always lands on a section; an absent `?tab` is the compact nav list, reached by going back. */
  const goToCenter = async (section?: string) => {
    if (!eventId.value || !activeExhibitorId.value) return
    await CPNavigateTo({
      name: 'clientportal-exhibitor-center',
      params: { eventId: eventId.value },
      query: { exhibitor: activeExhibitorId.value, tab: toExhibitorCenterSectionId(section ?? '') },
    })
  }

  const goToEvent = async () => {
    if (!eventId.value) return
    await CPNavigateTo({
      name: 'clientportal-events-detail',
      params: { eventId: eventId.value },
      hash: 'exhibitors',
    })
  }

  return {
    eventId,
    canViewEvents,
    queryEventId,
    adminMemberships,
    hasAdminAccess,
    isSettled,
    requestedExhibitorId,
    activeExhibitorId,
    isAdminOf,
    isMemberOf,
    goToCenter,
    goToEvent,
  }
}
